/* eslint-disable no-redeclare */

/**
 * Resolves the next time any provided event type fires on `node`, then removes
 * the listener.
 *
 * Accepts either a single event type or an array of event types. Useful when
 * you want to `await` a one-time DOM event instead of manually adding and
 * removing a listener.
 *
 * Examples:
 * ```ts
 * await once(image, 'load')
 * await once(dialog, ['transitionend', 'transitioncancel'])
 * await once(dialog, 'transitionend')
 * once(dialog, 'transitionend', () => { dialog.hide() })
 * ```
 *
 * If `handler` is provided, it runs after the promise resolves.
 */
export function once(node: Node, eventType: string, handler?: (event: Event) => void | Promise<void>): Promise<Event>;
export function once(node: Node, eventTypes: string[], handler?: (event: Event) => void | Promise<void>): Promise<Event>;
export function once(node: Node, eventTypeOrTypes: string | string[], handler?: (event: Event) => void | Promise<void>) {
    const eventTypes = Array.isArray(eventTypeOrTypes) ? eventTypeOrTypes : [eventTypeOrTypes];

    return new Promise<Event>(resolve => {
        const callback = (event: Event) => {
            eventTypes.forEach(eventType => node.removeEventListener(eventType, callback));

            resolve(event);
            handler?.(event);
        };

        eventTypes.forEach(eventType => node.addEventListener(eventType, callback));
    });
}

/**
 * Subscribes to one or more event types on `node` and returns an
 * `unsubscribe()` function.
 *
 * Accepts either a single event type or an array of event types. Useful when
 * you want a small wrapper around `addEventListener` that also passes
 * `unsubscribe()` to the handler so it can remove its own listener.
 *
 * Examples:
 * ```ts
 * const unsubscribe = on(window, 'resize', () => { layout() })
 * on(video, ['ended', 'error'], () => { cleanup() })
 * on(dialog, 'keydown', (event, unsubscribe) => {
 *     if (event.key === 'Escape') unsubscribe()
 * })
 * ```
 */
export function on(node: Node, eventType: string, handler: (event: Event, unsubscribe: () => void) => void | Promise<void>): () => void;
export function on(node: Node, eventTypes: string[], handler: (event: Event, unsubscribe: () => void) => void | Promise<void>): () => void;
export function on(node: Node, eventTypeOrTypes: string | string[], handler: (event: Event, unsubscribe: () => void) => void | Promise<void>) {
    const eventTypes = Array.isArray(eventTypeOrTypes) ? eventTypeOrTypes : [eventTypeOrTypes];

    const callback = (event: Event) => {
        void handler(event, unsubscribe);
    };

    const unsubscribe = () => {
        eventTypes.forEach(eventType => node.removeEventListener(eventType, callback));
    };

    eventTypes.forEach(eventType => node.addEventListener(eventType, callback));

    return unsubscribe;
}
